import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  limit as firestoreLimit,
  type Unsubscribe
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  RestaurantApplication, 
  RestaurantProfile, 
  Order,
  PlatformStats,
  DeliveryPartner,
  DeliveryArea,
  TimeSlot,
  Settlement,
  PlatformCommissionSettings
} from '../types';

// Collection names
const COLLECTIONS = {
  APPLICATIONS: 'restaurant_applications',
  RESTAURANTS: 'restaurants',
  ORDERS: 'orders',
  ADMINS: 'admins',
  DELIVERY_PARTNERS: 'delivery_partners',
  APP_SETTINGS: 'appSettings',
  DELIVERY_AREAS: 'delivery_areas',
  TIME_SLOTS: 'time_slots',
  SETTLEMENTS: 'settlements',
};

// ==================== APPLICATIONS ====================

/**
 * Get all applications
 */
export async function getAllApplications(): Promise<RestaurantApplication[]> {
  if (!db) throw new Error('Database not initialized');

  try {
    const q = query(
      collection(db, COLLECTIONS.APPLICATIONS),
      orderBy('submittedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const applications: RestaurantApplication[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      applications.push({
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate() || new Date(),
        reviewedAt: data.reviewedAt?.toDate(),
      } as RestaurantApplication);
    });

    return applications;
  } catch (error) {
    console.error('[Admin Service] Error fetching applications:', error);
    return [];
  }
}

/**
 * Get pending applications
 */
export async function getPendingApplications(): Promise<RestaurantApplication[]> {
  if (!db) throw new Error('Database not initialized');

  try {
    const q = query(
      collection(db, COLLECTIONS.APPLICATIONS),
      where('status', '==', 'pending'),
      orderBy('submittedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const applications: RestaurantApplication[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      applications.push({
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate() || new Date(),
        reviewedAt: data.reviewedAt?.toDate(),
      } as RestaurantApplication);
    });

    return applications;
  } catch (error) {
    console.error('[Admin Service] Error fetching pending applications:', error);
    return [];
  }
}

/**
 * Approve application
 */
export async function approveApplication(
  applicationId: string,
  adminUid: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  try {
    const applicationRef = doc(db, COLLECTIONS.APPLICATIONS, applicationId);
    const applicationSnap = await getDoc(applicationRef);
    
    if (!applicationSnap.exists()) {
      throw new Error('Application not found');
    }

    const applicationData = applicationSnap.data() as RestaurantApplication;

    // Update application status
    await updateDoc(applicationRef, {
      status: 'approved',
      reviewedAt: serverTimestamp(),
      reviewedBy: adminUid,
    });

    // Create restaurant profile
    const restaurantProfile: Partial<RestaurantProfile> = {
      uid: applicationData.uid,
      name: applicationData.restaurantName,
      email: applicationData.email,
      phoneNumber: applicationData.phoneNumber,
      image: applicationData.restaurantImages[0] || '',
      rating: 0,
      totalReviews: 0,
      deliveryTime: '30-40 mins',
      cuisine: applicationData.cuisine.join(', '),
      cuisineTypes: applicationData.cuisine,
      description: applicationData.description,
      location: {
        latitude: applicationData.location.latitude,
        longitude: applicationData.location.longitude,
        address: applicationData.address,
        city: applicationData.city,
        state: applicationData.state,
        pincode: applicationData.pincode,
      },
      isActive: false,
      isOpen: false,
      fssaiLicense: applicationData.fssaiLicense,
      gstNumber: applicationData.gstNumber,
      bankDetails: {
        accountNumber: applicationData.bankAccountNumber,
        ifscCode: applicationData.ifscCode,
        accountHolderName: applicationData.ownerName,
      },
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
      approvedAt: serverTimestamp() as any,
      totalOrders: 0,
      totalRevenue: 0,
      averageRating: 0,
      commissionPercentage: 10, // Default 10% platform commission
      razorpayAccountStatus: 'not_created',
    };

    await setDoc(doc(db, COLLECTIONS.RESTAURANTS, applicationData.uid), restaurantProfile);
    
    console.log('[Admin Service] Application approved');
  } catch (error) {
    console.error('[Admin Service] Error approving application:', error);
    throw error;
  }
}

/**
 * Reject application
 */
export async function rejectApplication(
  applicationId: string,
  adminUid: string,
  reason: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  try {
    const applicationRef = doc(db, COLLECTIONS.APPLICATIONS, applicationId);
    
    await updateDoc(applicationRef, {
      status: 'rejected',
      rejectionReason: reason,
      reviewedAt: serverTimestamp(),
      reviewedBy: adminUid,
    });

    console.log('[Admin Service] Application rejected');
  } catch (error) {
    console.error('[Admin Service] Error rejecting application:', error);
    throw error;
  }
}

// ==================== RESTAURANTS ====================

/**
 * Get all restaurants
 */
export async function getAllRestaurants(): Promise<RestaurantProfile[]> {
  if (!db) throw new Error('Database not initialized');

  try {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.RESTAURANTS));
    const restaurants: RestaurantProfile[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      restaurants.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate() || new Date(),
      } as RestaurantProfile);
    });

    return restaurants;
  } catch (error) {
    console.error('[Admin Service] Error fetching restaurants:', error);
    return [];
  }
}

/**
 * Toggle restaurant status
 */
export async function toggleRestaurantStatus(
  restaurantId: string,
  isActive: boolean
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  try {
    const docRef = doc(db, COLLECTIONS.RESTAURANTS, restaurantId);
    await updateDoc(docRef, {
      isActive,
      updatedAt: serverTimestamp(),
    });

    console.log('[Admin Service] Restaurant status updated');
  } catch (error) {
    console.error('[Admin Service] Error updating restaurant status:', error);
    throw error;
  }
}

// ==================== ORDERS ====================

/**
 * Get all orders - handles both old (orderedAt) and new (createdAt) field formats
 */
export async function getAllOrders(): Promise<Order[]> {
  if (!db) throw new Error('Database not initialized');

  try {
    // Try with createdAt first (customer app format)
    const q = query(
      collection(db, COLLECTIONS.ORDERS),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const orders: Order[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Handle both timestamp formats
      const timestamp = data.createdAt || data.orderedAt;
      orders.push({
        id: doc.id,
        customerId: data.userId || data.customerId || '',
        customerName: data.customerName || 'Customer',
        customerPhone: data.customerPhone || '',
        restaurantId: data.restaurantId || '',
        restaurantName: data.restaurantName || '',
        items: data.items || [],
        // Handle both totalAmount and grandTotal
        totalAmount: data.grandTotal || data.totalAmount || 0,
        deliveryAddress: data.deliveryAddress || { address: '', latitude: 0, longitude: 0 },
        status: data.status || 'placed',
        paymentMethod: data.paymentMethod || 'online',
        paymentStatus: data.paymentStatus || 'pending',
        orderedAt: timestamp?.toDate?.() || new Date(timestamp) || new Date(),
        acceptedAt: data.acceptedAt?.toDate?.(),
        preparingAt: data.preparingAt?.toDate?.(),
        readyAt: data.readyAt?.toDate?.(),
        pickedUpAt: data.pickedUpAt?.toDate?.(),
        deliveredAt: data.deliveredAt?.toDate?.() || data.actualDeliveryTime?.toDate?.(),
        cancelledAt: data.cancelledAt?.toDate?.(),
        // Additional fields from customer app
        deliveryPartner: data.deliveryPartner,
        deliveryOtp: data.deliveryOtp,
        orderId: data.orderId,
      } as Order);
    });

    return orders;
  } catch (error) {
    console.error('[Admin Service] Error fetching orders:', error);
    return [];
  }
}

// ==================== PLATFORM STATS ====================

/**
 * Get platform statistics
 */
export async function getPlatformStats(): Promise<PlatformStats> {
  if (!db) throw new Error('Database not initialized');

  try {
    const [restaurants, applications, orders, deliveryPartners] = await Promise.all([
      getAllRestaurants(),
      getAllApplications(),
      getAllOrders(),
      getAllDeliveryPartners()
    ]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayOrders = orders.filter(o => o.orderedAt >= today);
    const pendingApps = applications.filter(app => app.status === 'pending');
    const pendingDPApps = deliveryPartners.filter(dp => dp.verificationStatus === 'pending');

    // Calculate orders by restaurant
    const ordersByRestaurantMap = new Map<string, { restaurantName: string; orderCount: number; revenue: number }>();
    orders.forEach(order => {
      const key = order.restaurantId;
      const existing = ordersByRestaurantMap.get(key) || { restaurantName: order.restaurantName || 'Unknown', orderCount: 0, revenue: 0 };
      existing.orderCount++;
      existing.revenue += order.totalAmount || 0;
      ordersByRestaurantMap.set(key, existing);
    });

    const ordersByRestaurant = Array.from(ordersByRestaurantMap.entries())
      .map(([restaurantId, data]) => ({
        restaurantId,
        restaurantName: data.restaurantName,
        orderCount: data.orderCount,
        revenue: data.revenue
      }))
      .sort((a, b) => b.orderCount - a.orderCount);

    // Calculate commission stats
    let totalPlatformCommission = 0;
    let totalRestaurantPayouts = 0;
    const restaurantMap = new Map(restaurants.map(r => [r.id || r.uid, r]));
    orders.forEach(order => {
      const rest = restaurantMap.get(order.restaurantId);
      const commPct = rest?.commissionPercentage || 10;
      const amount = order.totalAmount || 0;
      const commission = Math.round((amount * commPct) / 100);
      totalPlatformCommission += commission;
      totalRestaurantPayouts += (amount - commission);
    });

    // Count pending settlements
    let pendingSettlements = 0;
    try {
      const settSnap = await getDocs(
        query(collection(db, COLLECTIONS.SETTLEMENTS), where('status', '==', 'pending'))
      );
      pendingSettlements = settSnap.size;
    } catch { /* ignore */ }

    return {
      totalRestaurants: restaurants.length,
      activeRestaurants: restaurants.filter(r => r.isActive).length,
      pendingApplications: pendingApps.length,
      totalOrders: orders.length,
      totalRevenue: orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0),
      todayOrders: todayOrders.length,
      todayRevenue: todayOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0),
      totalDeliveryPartners: deliveryPartners.length,
      activeDeliveryPartners: deliveryPartners.filter(dp => dp.isActive && dp.verificationStatus === 'approved').length,
      onlineDeliveryPartners: deliveryPartners.filter(dp => dp.isOnline).length,
      pendingDeliveryPartnerApplications: pendingDPApps.length,
      ordersByRestaurant,
      totalPlatformCommission,
      totalRestaurantPayouts,
      pendingSettlements,
    };
  } catch (error) {
    console.error('[Admin Service] Error fetching platform stats:', error);
    return {
      totalRestaurants: 0,
      activeRestaurants: 0,
      pendingApplications: 0,
      totalOrders: 0,
      totalRevenue: 0,
      todayOrders: 0,
      todayRevenue: 0,
      totalDeliveryPartners: 0,
      activeDeliveryPartners: 0,
      onlineDeliveryPartners: 0,
      pendingDeliveryPartnerApplications: 0,
      ordersByRestaurant: [],
      totalPlatformCommission: 0,
      totalRestaurantPayouts: 0,
      pendingSettlements: 0,
    };
  }
}

/**
 * Check if user is admin
 */
export async function checkAdminRole(uid: string): Promise<boolean> {
  if (!db) throw new Error('Database not initialized');

  try {
    const adminRef = doc(db, COLLECTIONS.ADMINS, uid);
    const adminSnap = await getDoc(adminRef);
    
    return adminSnap.exists();
  } catch (error) {
    console.error('[Admin Service] Error checking admin role:', error);
    return false;
  }
}

// ==================== DELIVERY PARTNERS ====================

/**
 * Get all delivery partners
 */
export async function getAllDeliveryPartners(): Promise<DeliveryPartner[]> {
  if (!db) throw new Error('Database not initialized');

  try {
    const q = query(
      collection(db, COLLECTIONS.DELIVERY_PARTNERS),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const partners: DeliveryPartner[] = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      partners.push({
        uid: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt) || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt) || new Date(),
        reviewedAt: data.reviewedAt?.toDate?.(),
        currentLocation: data.currentLocation ? {
          ...data.currentLocation,
          lastUpdated: data.currentLocation.lastUpdated?.toDate?.() || new Date()
        } : undefined,
      } as DeliveryPartner);
    });

    return partners;
  } catch (error) {
    console.error('[Admin Service] Error fetching delivery partners:', error);
    return [];
  }
}

/**
 * Get pending delivery partner applications
 */
export async function getPendingDeliveryPartners(): Promise<DeliveryPartner[]> {
  if (!db) throw new Error('Database not initialized');

  try {
    const q = query(
      collection(db, COLLECTIONS.DELIVERY_PARTNERS),
      where('verificationStatus', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const partners: DeliveryPartner[] = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      partners.push({
        uid: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as DeliveryPartner);
    });

    return partners;
  } catch (error) {
    console.error('[Admin Service] Error fetching pending delivery partners:', error);
    return [];
  }
}

/**
 * Approve delivery partner application
 */
export async function approveDeliveryPartner(
  partnerUid: string,
  adminUid: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  try {
    const partnerRef = doc(db, COLLECTIONS.DELIVERY_PARTNERS, partnerUid);
    
    await updateDoc(partnerRef, {
      verificationStatus: 'approved',
      isVerified: true,
      isActive: true,
      reviewedAt: serverTimestamp(),
      reviewedBy: adminUid,
    });

    console.log('[Admin Service] Delivery partner approved:', partnerUid);
  } catch (error) {
    console.error('[Admin Service] Error approving delivery partner:', error);
    throw error;
  }
}

/**
 * Reject delivery partner application
 */
export async function rejectDeliveryPartner(
  partnerUid: string,
  adminUid: string,
  reason: string
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  try {
    const partnerRef = doc(db, COLLECTIONS.DELIVERY_PARTNERS, partnerUid);
    
    await updateDoc(partnerRef, {
      verificationStatus: 'rejected',
      isVerified: false,
      isActive: false,
      rejectionReason: reason,
      reviewedAt: serverTimestamp(),
      reviewedBy: adminUid,
    });

    console.log('[Admin Service] Delivery partner rejected:', partnerUid);
  } catch (error) {
    console.error('[Admin Service] Error rejecting delivery partner:', error);
    throw error;
  }
}

/**
 * Toggle delivery partner active status
 */
export async function toggleDeliveryPartnerStatus(
  partnerUid: string,
  isActive: boolean
): Promise<void> {
  if (!db) throw new Error('Database not initialized');

  try {
    const partnerRef = doc(db, COLLECTIONS.DELIVERY_PARTNERS, partnerUid);
    await updateDoc(partnerRef, {
      isActive,
      updatedAt: serverTimestamp(),
    });

    console.log('[Admin Service] Delivery partner status updated:', partnerUid);
  } catch (error) {
    console.error('[Admin Service] Error updating delivery partner status:', error);
    throw error;
  }
}

/**
 * Subscribe to real-time delivery partner updates
 */
export function subscribeToDeliveryPartners(
  callback: (partners: DeliveryPartner[]) => void
): Unsubscribe {
  if (!db) throw new Error('Database not initialized');

  const q = query(
    collection(db, COLLECTIONS.DELIVERY_PARTNERS),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const partners: DeliveryPartner[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      partners.push({
        uid: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        currentLocation: data.currentLocation ? {
          ...data.currentLocation,
          lastUpdated: data.currentLocation.lastUpdated?.toDate?.() || new Date()
        } : undefined,
      } as DeliveryPartner);
    });
    callback(partners);
  });
}

/**
 * Subscribe to real-time order updates
 */
export function subscribeToOrders(
  callback: (orders: Order[]) => void
): Unsubscribe {
  if (!db) throw new Error('Database not initialized');

  const q = query(
    collection(db, COLLECTIONS.ORDERS),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const orders: Order[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const timestamp = data.createdAt || data.orderedAt;
      orders.push({
        id: docSnap.id,
        customerId: data.userId || data.customerId || '',
        customerName: data.customerName || 'Customer',
        customerPhone: data.customerPhone || '',
        restaurantId: data.restaurantId || '',
        restaurantName: data.restaurantName || '',
        items: data.items || [],
        totalAmount: data.grandTotal || data.totalAmount || 0,
        deliveryAddress: data.deliveryAddress || { address: '', latitude: 0, longitude: 0 },
        status: data.status || 'placed',
        paymentMethod: data.paymentMethod || 'online',
        paymentStatus: data.paymentStatus || 'pending',
        orderedAt: timestamp?.toDate?.() || new Date(),
        deliveryPartner: data.deliveryPartner,
        deliveryOtp: data.deliveryOtp,
        orderId: data.orderId,
      } as Order);
    });
    callback(orders);
  });
}

// ─── App Settings ──────────────────────────────────────────────────

export interface AppDeliverySettings {
  maxOrderLimit: number;
  deliveryRadius: number; // km radius for delivery partner order visibility
}

export async function getDeliverySettings(): Promise<AppDeliverySettings> {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.APP_SETTINGS, 'delivery'));
    if (snap.exists()) {
      return snap.data() as AppDeliverySettings;
    }
  } catch (error) {
    console.error('Error fetching delivery settings:', error);
  }
  return { maxOrderLimit: 5, deliveryRadius: 5 };
}

export async function updateDeliverySettings(settings: Partial<AppDeliverySettings>): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.APP_SETTINGS, 'delivery'), {
    ...settings,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ─── Notifications ────────────────────────────────────────────────────────────

export type AppType = 'delivery' | 'restaurant' | 'customer';

export interface AppUser {
  uid: string;
  name: string;
  email?: string;
  phone?: string;
  app: AppType;
}

const APP_USER_COLLECTIONS: Record<AppType, { col: string; nameField: string; emailField?: string; phoneField?: string }> = {
  delivery:   { col: 'delivery_partners', nameField: 'name',        phoneField: 'phoneNumber' },
  restaurant: { col: 'restaurants',       nameField: 'name',        emailField: 'email', phoneField: 'phoneNumber' },
  customer:   { col: 'users',             nameField: 'displayName', emailField: 'email', phoneField: 'phoneNumber' },
};

/** Fetches user list (uid, name, phone, email) from Firestore for the given apps. */
export async function getAppUsers(apps: AppType[]): Promise<AppUser[]> {
  if (!db) throw new Error('Database not initialized');
  const results: AppUser[] = [];

  for (const app of apps) {
    const info = APP_USER_COLLECTIONS[app];
    if (!info) continue;
    try {
      const snap = await getDocs(collection(db, info.col));
      snap.forEach((d) => {
        const data = d.data();
        results.push({
          uid: d.id,
          name: data[info.nameField] || data.name || d.id,
          email: info.emailField ? data[info.emailField] : undefined,
          phone: info.phoneField ? data[info.phoneField] : undefined,
          app,
        });
      });
    } catch (err) {
      console.error(`[Notify] Error fetching ${info.col}:`, err);
    }
  }
  return results;
}

const SERVER_URL = (import.meta as any).env?.VITE_SERVER_URL ?? 'https://server.lomra.in';

export interface SendNotificationPayload {
  apps: AppType[];
  userIds: string[] | 'all';
  title: string;
  body: string;
  imageUrl?: string;
  data?: Record<string, string>;
}

export interface SendNotificationResult {
  sent: number;
  failed: number;
  total: number;
  message: string;
  failures?: { token: string; error: string }[];
}

/** Sends a broadcast push notification via the LOMRA server. */
export async function sendNotification(payload: SendNotificationPayload): Promise<SendNotificationResult> {
  const res = await fetch(`${SERVER_URL}/api`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-lomra-app-key': 'LOMRA-SECURE-2026-APP',
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || `Server error ${res.status}`);
  }
  return json.data as SendNotificationResult;
}

// ==================== DELIVERY AREAS ====================

export async function getAllDeliveryAreas(): Promise<DeliveryArea[]> {
  if (!db) throw new Error('Database not initialized');
  try {
    const q = query(collection(db, COLLECTIONS.DELIVERY_AREAS), orderBy('name', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.() || new Date(),
    } as DeliveryArea));
  } catch (error) {
    console.error('[Admin Service] Error fetching delivery areas:', error);
    return [];
  }
}

export function subscribeToDeliveryAreas(callback: (areas: DeliveryArea[]) => void): Unsubscribe {
  if (!db) throw new Error('Database not initialized');
  const q = query(collection(db, COLLECTIONS.DELIVERY_AREAS), orderBy('name', 'asc'));
  return onSnapshot(q, (snap) => {
    const areas = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.() || new Date(),
    } as DeliveryArea));
    callback(areas);
  });
}

export async function createDeliveryArea(area: Omit<DeliveryArea, 'id' | 'createdAt'>): Promise<string> {
  if (!db) throw new Error('Database not initialized');
  const docRef = doc(collection(db, COLLECTIONS.DELIVERY_AREAS));
  await setDoc(docRef, {
    ...area,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateDeliveryArea(areaId: string, data: Partial<DeliveryArea>): Promise<void> {
  if (!db) throw new Error('Database not initialized');
  const { id, createdAt, ...cleanData } = data as any;
  await updateDoc(doc(db, COLLECTIONS.DELIVERY_AREAS, areaId), cleanData);
}

export async function deleteDeliveryArea(areaId: string): Promise<void> {
  if (!db) throw new Error('Database not initialized');
  await deleteDoc(doc(db, COLLECTIONS.DELIVERY_AREAS, areaId));
}

// ==================== TIME SLOTS ====================

export async function getTimeSlotsForDate(date: string, areaId?: string): Promise<TimeSlot[]> {
  if (!db) throw new Error('Database not initialized');
  try {
    let q;
    if (areaId) {
      q = query(
        collection(db, COLLECTIONS.TIME_SLOTS),
        where('date', '==', date),
        where('areaId', '==', areaId)
      );
    } else {
      q = query(collection(db, COLLECTIONS.TIME_SLOTS), where('date', '==', date));
    }
    const snap = await getDocs(q);
    return snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.() || new Date(),
    } as TimeSlot));
  } catch (error) {
    console.error('[Admin Service] Error fetching time slots:', error);
    return [];
  }
}

// ==================== COMMISSION MANAGEMENT ====================

/**
 * Update restaurant commission percentage
 */
export async function updateRestaurantCommission(
  restaurantId: string,
  commissionPercentage: number
): Promise<void> {
  if (!db) throw new Error('Database not initialized');
  if (commissionPercentage < 0 || commissionPercentage > 100) {
    throw new Error('Commission percentage must be between 0 and 100');
  }

  await updateDoc(doc(db, COLLECTIONS.RESTAURANTS, restaurantId), {
    commissionPercentage,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get platform commission settings
 */
export async function getPlatformCommissionSettings(): Promise<PlatformCommissionSettings> {
  if (!db) throw new Error('Database not initialized');
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.APP_SETTINGS, 'commission'));
    if (snap.exists()) {
      return snap.data() as PlatformCommissionSettings;
    }
  } catch (error) {
    console.error('[Admin Service] Error fetching commission settings:', error);
  }
  return {
    defaultCommissionPercentage: 10,
    minCommissionPercentage: 5,
    maxCommissionPercentage: 40,
    deliveryPartnerPayPerOrder: 30,
    deliveryPartnerPayPerKm: 7,
  };
}

/**
 * Update platform commission settings
 */
export async function updatePlatformCommissionSettings(
  settings: Partial<PlatformCommissionSettings>
): Promise<void> {
  if (!db) throw new Error('Database not initialized');
  await setDoc(doc(db, COLLECTIONS.APP_SETTINGS, 'commission'), {
    ...settings,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Get restaurants ranked by commission (highest first)
 */
export async function getRestaurantsByCommission(): Promise<RestaurantProfile[]> {
  if (!db) throw new Error('Database not initialized');
  const restaurants = await getAllRestaurants();
  return restaurants.sort((a, b) => (b.commissionPercentage || 0) - (a.commissionPercentage || 0));
}

// ==================== SETTLEMENTS ====================

/**
 * Get all settlements (from Firestore)
 */
export async function getAllSettlements(limitCount = 50): Promise<Settlement[]> {
  if (!db) throw new Error('Database not initialized');
  try {
    const q = query(
      collection(db, COLLECTIONS.SETTLEMENTS),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      periodStart: d.data().periodStart?.toDate?.() || new Date(),
      periodEnd: d.data().periodEnd?.toDate?.() || new Date(),
      settledAt: d.data().settledAt?.toDate?.(),
      createdAt: d.data().createdAt?.toDate?.() || new Date(),
      updatedAt: d.data().updatedAt?.toDate?.() || new Date(),
    } as Settlement));
  } catch (error) {
    console.error('[Admin Service] Error fetching settlements:', error);
    return [];
  }
}

/**
 * Get settlements for a specific restaurant
 */
export async function getRestaurantSettlements(restaurantId: string): Promise<Settlement[]> {
  if (!db) throw new Error('Database not initialized');
  try {
    const q = query(
      collection(db, COLLECTIONS.SETTLEMENTS),
      where('restaurantId', '==', restaurantId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      periodStart: d.data().periodStart?.toDate?.() || new Date(),
      periodEnd: d.data().periodEnd?.toDate?.() || new Date(),
      settledAt: d.data().settledAt?.toDate?.(),
      createdAt: d.data().createdAt?.toDate?.() || new Date(),
      updatedAt: d.data().updatedAt?.toDate?.() || new Date(),
    } as Settlement));
  } catch (error) {
    console.error('[Admin Service] Error fetching restaurant settlements:', error);
    return [];
  }
}

/**
 * Generate settlement via server API
 */
export async function generateSettlement(
  restaurantId: string,
  periodStart: string,
  periodEnd: string
): Promise<Settlement> {
  const res = await fetch(`${SERVER_URL}/api/settlements`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restaurantId, periodStart, periodEnd }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || `Server error ${res.status}`);
  }
  return json.data as Settlement;
}

/**
 * Update settlement status
 */
export async function updateSettlementStatus(
  settlementId: string,
  status: 'pending' | 'processing' | 'settled' | 'failed'
): Promise<void> {
  if (!db) throw new Error('Database not initialized');
  const updateData: Record<string, any> = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (status === 'settled') {
    updateData.settledAt = serverTimestamp();
  }
  await updateDoc(doc(db, COLLECTIONS.SETTLEMENTS, settlementId), updateData);
}

// ==================== RAZORPAY LINKED ACCOUNTS ====================

/**
 * Create a Razorpay linked account for a restaurant via server
 */
export async function createLinkedAccount(
  entityId: string,
  entityType: 'restaurant' | 'delivery_partner',
  data: {
    email: string;
    phone: string;
    legalBusinessName: string;
    contactName: string;
    businessType?: string;
    legalInfo?: { pan?: string; gst?: string };
    bankAccount?: {
      ifscCode: string;
      beneficiaryName: string;
      accountNumber: string;
      accountType: 'current' | 'savings';
    };
  }
): Promise<{ accountId: string; status: string }> {
  const res = await fetch(`${SERVER_URL}/api/razorpay/linked-accounts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...data,
      entityId,
      entityType,
    }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || `Server error ${res.status}`);
  }

  // Save linked account ID to the entity document in Firestore
  const col = entityType === 'restaurant' ? COLLECTIONS.RESTAURANTS : COLLECTIONS.DELIVERY_PARTNERS;
  await updateDoc(doc(db, col, entityId), {
    razorpayLinkedAccountId: json.data.accountId,
    razorpayAccountStatus: json.data.status || 'created',
    updatedAt: serverTimestamp(),
  });

  return json.data;
}

/**
 * Fetch Razorpay linked account details from server
 */
export async function getLinkedAccountDetails(
  accountId: string
): Promise<any> {
  const res = await fetch(`${SERVER_URL}/api/razorpay/linked-accounts?accountId=${encodeURIComponent(accountId)}`);
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || `Server error ${res.status}`);
  }
  return json.data;
}

// ==================== USERS (CUSTOMER APP) ====================

/**
 * Get all customer app users
 */
export async function getAllCustomers(): Promise<any[]> {
  if (!db) throw new Error('Database not initialized');
  try {
    const snap = await getDocs(collection(db, 'users'));
    return snap.docs.map(d => ({
      uid: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.() || new Date(),
      updatedAt: d.data().updatedAt?.toDate?.() || new Date(),
    }));
  } catch (error) {
    console.error('[Admin Service] Error fetching customers:', error);
    return [];
  }
}
